import { Vector3 } from 'three';
import { TrackRuntime } from '../track/runtime';
import { ARROW_PROFILE, trackEnvelopeRadius } from '../geometry/profile';
import { worldMetersToPrintMm, printMmToWorldMetres } from './types';
import { JOINT_PIN_LENGTH_M, JOINT_SOCKET_PLUG_M } from './joints';

const SAMPLE_STEP_M = 0.5;

export interface ObbMm {
  x: number;
  y: number;
  z: number;
}

export interface ProposeTrackCutsOpts {
  /**
   * Arc-length positions of support↔track connections (world metres).
   * Interior cuts are placed only at the midpoint between consecutive stations.
   */
  supportAttachS?: number[];
  /**
   * Unused: track cuts are mid-support only, so they already sit as far from
   * attaches as the span allows.
   */
  supportCutClearanceMm?: number;
  /**
   * Rack pitch (world metres). When set, mid-support cuts snap to the nearest
   * tooth bottom (valley at n·pitch) still between those two supports.
   */
  toothPitchM?: number;
}

/** Attach points closer than this (world m) count as one support station. */
const SUPPORT_STATION_CLUSTER_M = 1;

/** Max side of the print-mm OBB (local frame of the segment midpoint). */
export function obbMaxSideMm(obb: ObbMm): number {
  return Math.max(obb.x, obb.y, obb.z);
}

/**
 * Leave room for a click-fit pin/sleeve so a flush cube fit does not overflow
 * after joints are added.
 */
export function printChunkFitLimitMm(chunkSizeMm: number): number {
  const jointBudgetMm = worldMetersToPrintMm(JOINT_PIN_LENGTH_M + JOINT_SOCKET_PLUG_M);
  return Math.max(chunkSizeMm - jointBudgetMm, chunkSizeMm * 0.75);
}

/** Shortest track interval we will emit, in world metres. */
export function minTrackChunkArcM(chunkSizeMm: number): number {
  return Math.max(printMmToWorldMetres(chunkSizeMm) * 0.1, SAMPLE_STEP_M * 4);
}

/**
 * PCA-oriented bounding box of world-metre points, in print mm.
 * Longest side is the printable extent after rotating the part onto the cube.
 */
export function pointsObbMm(points: Vector3[]): ObbMm {
  if (points.length === 0) return { x: 0, y: 0, z: 0 };

  const c = new Vector3();
  for (const p of points) c.add(p);
  c.multiplyScalar(1 / points.length);

  let xx = 0;
  let xy = 0;
  let xz = 0;
  let yy = 0;
  let yz = 0;
  let zz = 0;
  for (const p of points) {
    const dx = p.x - c.x;
    const dy = p.y - c.y;
    const dz = p.z - c.z;
    xx += dx * dx;
    xy += dx * dy;
    xz += dx * dz;
    yy += dy * dy;
    yz += dy * dz;
    zz += dz * dz;
  }
  const inv = 1 / points.length;
  const axes = principalAxes(xx * inv, xy * inv, xz * inv, yy * inv, yz * inv, zz * inv);

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  const d = new Vector3();
  for (const p of points) {
    d.subVectors(p, c);
    const x = d.dot(axes[0]);
    const y = d.dot(axes[1]);
    const z = d.dot(axes[2]);
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  return {
    x: worldMetersToPrintMm(maxX - minX),
    y: worldMetersToPrintMm(maxY - minY),
    z: worldMetersToPrintMm(maxZ - minZ),
  };
}

/**
 * Oriented bounding box of the track envelope on [s0, s1], in print mm.
 * Axes are the banked frame at the segment midpoint.
 */
export function trackSegmentObbMm(runtime: TrackRuntime, s0: number, s1: number): ObbMm {
  const L = runtime.totalLength;
  const a = Math.max(0, Math.min(s0, s1));
  const b = Math.min(L, Math.max(s0, s1));
  if (b - a < 1e-9) return { x: 0, y: 0, z: 0 };

  const mid = runtime.frameAt((a + b) / 2);
  const half = trackEnvelopeRadius(ARROW_PROFILE);
  const offsets: [number, number][] = [
    [-half, -half],
    [half, -half],
    [-half, half],
    [half, half],
    [0, 0],
  ];

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;

  const tmp = new Vector3();
  const d = new Vector3();
  const n = Math.max(2, Math.ceil((b - a) / SAMPLE_STEP_M));
  for (let i = 0; i <= n; i++) {
    const s = a + ((b - a) * i) / n;
    const f = runtime.frameAt(s);
    for (const [ox, oy] of offsets) {
      tmp.copy(f.railPos).addScaledVector(f.right, ox).addScaledVector(f.up, oy);
      d.subVectors(tmp, mid.pos);
      const x = d.dot(mid.right);
      const y = d.dot(mid.up);
      const z = d.dot(mid.tan);
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      if (z < minZ) minZ = z;
      if (z > maxZ) maxZ = z;
    }
  }

  return {
    x: worldMetersToPrintMm(maxX - minX),
    y: worldMetersToPrintMm(maxY - minY),
    z: worldMetersToPrintMm(maxZ - minZ),
  };
}

export function segmentFitsBuildVolume(
  runtime: TrackRuntime,
  s0: number,
  s1: number,
  chunkSizeMm: number,
): boolean {
  return obbMaxSideMm(trackSegmentObbMm(runtime, s0, s1)) <= chunkSizeMm + 1e-6;
}

/**
 * Propose cut arc lengths at the midpoint between consecutive support stations.
 *
 * Track is never split to fill the print cube, before U-turns, or for any
 * other reason. Zero or one station → a single [0, totalLength] interval.
 * Closed tracks also cut at the wrap-around midpoint between last and first.
 * When {@link ProposeTrackCutsOpts.toothPitchM} is set, each midpoint snaps
 * to the nearest rack tooth bottom still between those supports.
 *
 * Returns the full boundary list including 0 and totalLength.
 */
export function proposeTrackCuts(
  runtime: TrackRuntime,
  chunkSizeMm: number,
  opts?: ProposeTrackCutsOpts,
): number[] {
  void chunkSizeMm;
  const L = runtime.totalLength;
  if (L < 1e-6) return [0, 0];

  const toothPitchM =
    opts?.toothPitchM != null && Number.isFinite(opts.toothPitchM) && opts.toothPitchM > 0
      ? opts.toothPitchM
      : undefined;

  const stations = clusterSupportStations(opts?.supportAttachS ?? [], L, runtime.closed);
  if (stations.length < 2) return [0, L];

  const interiors: number[] = [];
  for (let i = 0; i < stations.length - 1; i++) {
    const a = stations[i]!;
    const b = stations[i + 1]!;
    const mid = (a + b) / 2;
    pushInteriorCut(interiors, snapMidpointToTooth(mid, a, b, L, toothPitchM), L);
  }
  if (runtime.closed) {
    const first = stations[0]!;
    const last = stations[stations.length - 1]!;
    const wrapSpan = L - last + first;
    if (wrapSpan > SUPPORT_STATION_CLUSTER_M) {
      const midUnwrapped = last + wrapSpan / 2;
      pushInteriorCut(
        interiors,
        snapMidpointToTooth(midUnwrapped, last, last + wrapSpan, L, toothPitchM),
        L,
      );
    }
  }

  return sortedUnique([0, ...interiors, L]);
}

/**
 * True when the closed-track s=0/L seam itself is the mid-support cut
 * (wrap midpoint landed on the station start). Then first and last remeshes
 * stay separate and get the wrap click-fit. Otherwise they belong to the same
 * support and should be merged.
 */
export function closedSeamIsBetweenSupports(
  runtime: TrackRuntime,
  supportAttachS?: number[],
): boolean {
  if (!runtime.closed) return false;
  const L = runtime.totalLength;
  const stations = clusterSupportStations(supportAttachS ?? [], L, true);
  if (stations.length < 2) return false;
  const mid = wrapMidpoint(stations, L);
  return mid <= 1e-6 || mid >= L - 1e-6;
}

/**
 * Nearest n·pitch valley inside `(lo, hi)`. Prefers the earlier valley on a
 * tie so a snap cannot pass the later support.
 */
export function nearestToothBottomInRange(
  s: number,
  pitch: number,
  lo: number,
  hi: number,
): number | null {
  const p = Math.max(pitch, 1e-3);
  if (hi <= lo + 1e-9) return null;
  const k0 = Math.ceil(lo / p - 1e-12);
  const k1 = Math.floor(hi / p + 1e-12);
  let best: number | null = null;
  let bestDist = Infinity;
  for (let k = k0; k <= k1; k++) {
    const v = k * p;
    if (v <= lo + 1e-12 || v >= hi - 1e-12) continue;
    const d = Math.abs(v - s);
    if (d < bestDist - 1e-12 || (Math.abs(d - bestDist) <= 1e-12 && (best == null || v < best))) {
      best = v;
      bestDist = d;
    }
  }
  return best;
}

function snapMidpointToTooth(
  mid: number,
  lo: number,
  hi: number,
  L: number,
  pitch: number | undefined,
): number {
  if (pitch == null) return wrapToLength(mid, L);
  const p = Math.max(pitch, 1e-3);
  const snapLo = Math.max(lo, mid - p);
  const snapHi = Math.min(hi, mid + p);
  const snapped = nearestToothBottomInRange(mid, p, snapLo, snapHi);
  return wrapToLength(snapped ?? mid, L);
}

function pushInteriorCut(cuts: number[], s: number, L: number): void {
  if (s > 1e-6 && s < L - 1e-6) cuts.push(s);
}

function wrapToLength(s: number, L: number): number {
  if (L <= 1e-12) return 0;
  let x = s % L;
  if (x < 0) x += L;
  if (x >= L - 1e-9) return 0;
  return x;
}

function wrapMidpoint(stations: number[], L: number): number {
  const first = stations[0]!;
  const last = stations[stations.length - 1]!;
  return wrapToLength(last + (L - last + first) / 2, L);
}

function clusterSupportStations(attaches: number[], L: number, closed: boolean): number[] {
  if (attaches.length === 0 || L <= 1e-12) return [];
  const mapped: number[] = [];
  for (const raw of attaches) {
    if (!Number.isFinite(raw)) continue;
    mapped.push(closed ? wrapToLength(raw, L) : Math.min(L, Math.max(0, raw)));
  }
  if (mapped.length === 0) return [];
  const sorted = sortedUnique(mapped);
  const groups: number[][] = [];
  let group: number[] = [sorted[0]!];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i]! - sorted[i - 1]! <= SUPPORT_STATION_CLUSTER_M) group.push(sorted[i]!);
    else {
      groups.push(group);
      group = [sorted[i]!];
    }
  }
  groups.push(group);
  if (closed && groups.length > 1) {
    const first = groups[0]!;
    const last = groups[groups.length - 1]!;
    const wrapGap = L - last[last.length - 1]! + first[0]!;
    if (wrapGap <= SUPPORT_STATION_CLUSTER_M) {
      groups[0] = last.concat(first);
      groups.pop();
    }
  }
  return groups.map((grp) => clusterMean(grp, L));
}

function clusterMean(grp: number[], L: number): number {
  if (grp.length === 1) return grp[0]!;
  const wrapped = grp[0]! > grp[grp.length - 1]!;
  let sum = 0;
  for (const s of grp) sum += wrapped && s < L * 0.5 ? s + L : s;
  return wrapToLength(sum / grp.length, L);
}

function sortedUnique(values: number[]): number[] {
  if (values.length === 0) return [];
  const sorted = [...values].sort((a, b) => a - b);
  const out: number[] = [sorted[0]!];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i]! - out[out.length - 1]! > 1e-6) out.push(sorted[i]!);
  }
  return out;
}

/** Orthonormal PCA axes of a 3×3 covariance (Jacobi). */
function principalAxes(
  xx: number,
  xy: number,
  xz: number,
  yy: number,
  yz: number,
  zz: number,
): [Vector3, Vector3, Vector3] {
  const A = [
    [xx, xy, xz],
    [xy, yy, yz],
    [xz, yz, zz],
  ];
  const V = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];

  for (let iter = 0; iter < 24; iter++) {
    let p = 0;
    let q = 1;
    let max = Math.abs(A[0][1]);
    const a02 = Math.abs(A[0][2]);
    const a12 = Math.abs(A[1][2]);
    if (a02 > max) {
      p = 0;
      q = 2;
      max = a02;
    }
    if (a12 > max) {
      p = 1;
      q = 2;
      max = a12;
    }
    if (max < 1e-15) break;

    const app = A[p][p];
    const aqq = A[q][q];
    const apq = A[p][q];
    const theta = 0.5 * Math.atan2(2 * apq, aqq - app);
    const c = Math.cos(theta);
    const s = Math.sin(theta);

    for (let i = 0; i < 3; i++) {
      if (i === p || i === q) continue;
      const aip = A[i][p];
      const aiq = A[i][q];
      A[i][p] = A[p][i] = c * aip - s * aiq;
      A[i][q] = A[q][i] = s * aip + c * aiq;
    }
    A[p][p] = c * c * app - 2 * s * c * apq + s * s * aqq;
    A[q][q] = s * s * app + 2 * s * c * apq + c * c * aqq;
    A[p][q] = A[q][p] = 0;

    for (let i = 0; i < 3; i++) {
      const vip = V[i][p];
      const viq = V[i][q];
      V[i][p] = c * vip - s * viq;
      V[i][q] = s * vip + c * viq;
    }
  }

  const ax0 = new Vector3(V[0][0], V[1][0], V[2][0]);
  const ax1 = new Vector3(V[0][1], V[1][1], V[2][1]);
  if (ax0.lengthSq() < 1e-18) ax0.set(1, 0, 0);
  else ax0.normalize();
  ax1.addScaledVector(ax0, -ax1.dot(ax0));
  if (ax1.lengthSq() < 1e-18) {
    const hint = Math.abs(ax0.x) < 0.9 ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0);
    ax1.crossVectors(ax0, hint);
  }
  ax1.normalize();
  const ax2 = new Vector3().crossVectors(ax0, ax1).normalize();
  return [ax0, ax1, ax2];
}
